import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Loader2, MapPin, ShoppingBag } from 'lucide-react';
import { getOrder, trackOrder } from '@/lib/api/endpoints';

const formatPrice = (paiseStr, currency = 'INR') => {
  const rupees = parseInt(paiseStr || '0', 10) / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(rupees);
};

// Normalizes the /track-order response into the same shape as the Store
// API's GET /order/{id} response, so the render code below only ever has
// to deal with one shape regardless of which source answered.
const normalizeTrackOrderResponse = (data) => ({
  id: data.id,
  status: data.status,
  totals: {
    currency_code: data.currency,
    total_price: data.total,
    total_shipping: data.shipping_total,
    total_tax: data.tax_total,
  },
  items: (data.line_items || []).map((item, i) => ({
    key: `track-${i}`,
    name: item.name,
    quantity: item.quantity,
    totals: { line_total: item.total },
  })),
});

// The Store API's guest lookup (key + billing_email) 403s with "belongs to
// a different customer" if the order got attributed to a real customer_id
// — e.g. whoever placed the order was also logged into wp-admin in the
// same browser tab, so their session cookie rode along on what was meant
// to be a same-origin-but-anonymous request. Real customers checking out
// as guests won't hit this; it's mainly an admin-testing footgun. Rather
// than block on it, fall back to /track-order, which verifies the email
// directly against the order and isn't affected by customer_id at all.
async function fetchOrder(id, key, billingEmail) {
  try {
    return await getOrder(id, key, billingEmail);
  } catch (err) {
    if (billingEmail) {
      try {
        const data = await trackOrder(id, billingEmail);
        return normalizeTrackOrderResponse(data);
      } catch {
        // Fall through to the original error below.
      }
    }
    throw err;
  }
}

const STATUS_LABELS = {
  pending: 'Pending',
  processing: 'Composing',
  'on-hold': 'On hold',
  completed: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  failed: 'Failed',
};

const OrderReceived = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const key = params.get('key');
  const billingEmail = params.get('billing_email');

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['order', id, key, billingEmail],
    queryFn: () => fetchOrder(id, key, billingEmail),
    enabled: !!id,
    retry: 1,
  });

  if (isLoading) {
    return (
      <main className="container-lux py-32 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline text-[#1a120b]/50" />
        <p className="mt-4 text-[#1a120b]/60">Loading your order…</p>
      </main>
    );
  }

  if (error || !order) {
    return (
      <main className="container-lux py-24 max-w-2xl text-center">
        <p className="font-display text-4xl mb-4">Order not found</p>
        <p className="text-[#1a120b]/60 mb-8">
          We couldn't find this order. It may take a few minutes to appear after
          payment — try again, or{' '}
          <Link to="/track-order" className="text-[#b78d3c] hover:underline">
            track by order ID
          </Link>
          .
        </p>
        <Link to="/shop" className="btn-primary">
          Continue shopping
        </Link>
      </main>
    );
  }

  // The Store API's GET /order/{id} response nests everything under
  // `totals` (currency_code, total_price, total_shipping, total_tax) and
  // calls the line items `items`, not `line_items` — a different shape
  // from the WC REST v3 / classic order schema this page was originally
  // written against. Mapping to the actual response here, not a guess.
  const totals = order.totals || {};
  const currency = totals.currency_code || 'INR';
  const status = order.status || 'processing';
  const statusLabel = STATUS_LABELS[status] || status;
  const lineItems = Array.isArray(order.items) ? order.items : [];
  const shippingTotal = parseInt(totals.total_shipping || '0', 10);
  const taxTotal = parseInt(totals.total_tax || '0', 10);

  return (
    <main className="container-lux py-16 md:py-24 max-w-3xl">
      <div className="text-center mb-14">
        <div className="w-20 h-20 rounded-full bg-[#1a120b] text-[#b78d3c] flex items-center justify-center mx-auto mb-8">
          <Check className="w-10 h-10" />
        </div>
        <p className="tracking-luxe text-[#b78d3c] mb-3">
          {status === 'pending' || status === 'on-hold' ? 'Order received' : 'Order confirmed'}
        </p>
        <h1 className="font-display text-5xl md:text-6xl mb-4">Thank you.</h1>
        <p className="text-[#3a2a1e]/75 max-w-md mx-auto text-lg leading-relaxed">
          Your box will now be composed by hand in Mumbai. A confirmation is on
          its way to your email.
        </p>
        <div className="mt-8 inline-block border border-[#1a120b]/20 px-8 py-4">
          <p className="tracking-luxe text-[#1a120b]/55 text-[10px]">Order ID</p>
          <p className="font-display text-2xl mt-1">#{order.id || order.number}</p>
        </div>
      </div>

      <div className="bg-[#efe6d3] p-8 mb-6">
        <p className="tracking-luxe text-[#b78d3c] text-[10px] mb-3">Status</p>
        <p className="font-display text-2xl">{statusLabel}</p>

        {lineItems.length > 0 && (
          <>
            <p className="tracking-luxe text-[#b78d3c] text-[10px] mt-6 mb-3">
              Items
            </p>
            <ul className="space-y-2">
              {lineItems.map((item, i) => (
                <li
                  key={item.key || i}
                  className="flex justify-between text-sm border-b border-[#1a120b]/10 pb-2 last:border-0"
                >
                  <span>
                    {item.name} × {item.quantity}
                  </span>
                  <span>{formatPrice(item.totals?.line_total, currency)}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="mt-6 space-y-2 text-sm">
          {taxTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-[#1a120b]/70">Tax</span>
              <span>{formatPrice(taxTotal, currency)}</span>
            </div>
          )}
          {shippingTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-[#1a120b]/70">Shipping</span>
              <span>{formatPrice(shippingTotal, currency)}</span>
            </div>
          )}
          <div className="flex justify-between font-display text-xl pt-2 border-t border-[#1a120b]/15">
            <span>Total</span>
            <span>{formatPrice(totals.total_price, currency)}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/shop" className="btn-ghost">
          <ShoppingBag className="w-4 h-4" /> Continue shopping
        </Link>
        <Link to="/track-order" className="btn-primary">
          <MapPin className="w-4 h-4" /> Track another order
        </Link>
      </div>
    </main>
  );
};

export default OrderReceived;
